import { AccessRights } from 'src/app/features/role/user-role/user-role'
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core'
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  NgForm,
  Validators
} from '@angular/forms'
import { combineLatest, Observable } from 'rxjs'
import { scrollToFirstInvalidControl } from 'src/app/shared/until'
import { Course } from '../course'
import { NotificationService } from 'src/app/core/notification.service'
import { environment } from 'src/environments/environment'
import { UntilDestroy } from '@ngneat/until-destroy'
import { AppState } from 'src/app/core/core.state'
import { Store } from '@ngrx/store'
import { map, startWith } from 'rxjs/operators'
import { productActions } from '../../product/product.actions'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { Product } from '../../product/product'
import { productSelectors } from '../../product/product.selectors'
import { FORM_LENGTH_BY_PRODUCT_TYPE } from 'src/app/shared/constants'
import moment from 'moment-mini'
import { Translation } from '../../translation/translation'
import { Tag } from '../../tag/tag'
import { tagSelectors } from '../../tag/tag.selectors'
import { translationSelectors } from '../../translation/translation.selectors'

@UntilDestroy()
@Component({
  selector: 'course-form-general',
  templateUrl: './form-general.component.html',
  styleUrls: ['./form-general.component.scss']
})
export class CourseFormComponent implements OnInit {
  @ViewChild('formRef') formRef: NgForm

  @Output() onSubmit = new EventEmitter<any>()
  @Input() course: Course
  @Input() showSubmitButton: boolean = true

  public courseFormValue: Course
  public showedCategories = [1, 2, 3]
  public hiddenCategories = [4, 5, 6, 7, 8, 9]
  public descriptionMaxLength: number =
    FORM_LENGTH_BY_PRODUCT_TYPE.course.description

  public form: UntypedFormGroup
  public showPrice: boolean = false
  public accessRights = AccessRights
  public hasExtendedCategories: boolean =
    environment.moduleConfigs.hasExtendedCategories
  public countryCurrency = environment.countryCurrency
    ? environment.countryCurrency
    : '€'

  public selectableProducts$: Observable<Product[]>
  public isLoadingProduct$: Observable<boolean>
  private didFetchProduct: boolean = false
  public selectedProduct$: Observable<Product>
  public selectedProductTags$: Observable<Tag[]>
  public selectedProductTranslations$: Observable<Translation[]>

  constructor(
    private store: Store<AppState>,
    private fb: UntypedFormBuilder,
    private elementRef: ElementRef,
    private notificationService: NotificationService
  ) {}

  ngOnInit() {
    this.form = this.fb.group({
      productId: [null, [Validators.required]],
      description: [
        null,
        [
          Validators.required,
          Validators.minLength(1),
          Validators.maxLength(this.descriptionMaxLength)
        ]
      ],
      startDate: [null, this.course?.id ? [Validators.required] : []],
      endDate: [null, this.course?.id ? [Validators.required] : []],
      availableFromDate: [null, [Validators.required]],
      availableTillDate: [null, [Validators.required]],
      startDateEnrollment: [null, [Validators.required]],
      endDateEnrollment: [null, [Validators.required]],
      price: [null, []],
      linkedSubsPrice: [null, []], // will be set on bookings creation
      category1Price: [null, []],
      category2Price: [null, []],
      category3Price: [null, []],
      category4Price: [null, []],
      category5Price: [null, []],
      category6Price: [null, []],
      category7Price: [null, []],
      category8Price: [null, []],
      category9Price: [null, []],
      vat: [null, []],
      password: [null, []]
    })

    this.selectableProducts$ = this.store
      .select(productSelectors.selectAll)
      .pipe(
        map((allProducts) =>
          allProducts.filter(
            (product) =>
              (Boolean(product.allowAsLinkedProduct) ||
                (!product.bookable && !product.allowAsLinkedProduct)) &&
              product.isActive &&
              Boolean(product.isCourse)
          )
        )
      )

    this.selectedProduct$ = combineLatest([
      this.store.select(productSelectors.selectEntities),
      this.form.controls.productId.valueChanges.pipe(startWith(null))
    ]).pipe(
      map(([productEntities, productId]) => {
        return productEntities[this.course?.productId || productId]
      })
    )

    this.selectedProductTranslations$ = combineLatest([
      this.selectedProduct$,
      this.store.select(translationSelectors.selectEntities)
    ]).pipe(
      map(([product, translationEntities]) => {
        if (!product?.translations?.length || translationEntities?.length) {
          return []
        }
        return product.translations.map((tId) => translationEntities[tId])
      })
    )

    this.selectedProductTags$ = combineLatest([
      this.selectedProduct$,
      this.store.select(tagSelectors.selectEntities)
    ]).pipe(
      map(([product, tagEntitities]) => {
        if (!product?.tags?.length) {
          return []
        }

        return product.tags.map((tId) => tagEntitities[tId])
      })
    )

    this.isLoadingProduct$ = this.store.select(productSelectors.selectIsLoading)
  }

  public ngOnChanges() {
    this.courseFormValue = this.course?.id
      ? {
          ...this.course,
          endDate: this.course.endDate
            ? moment(this.course.endDate).add(1, 'minute').toDate()
            : null
        }
      : this.course

    // if course product provided, fetch it so it can display in multiselect
    if (this.course?.productId && !this.didFetchProduct) {
      this.store.dispatch(
        productActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: { id: this.course?.productId },
            join: [{ field: 'translations' }, { field: 'tags' }]
          })
        })
      )
      this.didFetchProduct = true
    }
  }

  public onFormSubmit(): void {
    const f = this.form.value

    // validate dates

    if (!f.startDateEnrollment || !moment(f.startDateEnrollment).isValid()) {
      this.form.controls.startDateEnrollment.markAsDirty()
      this.form.controls.startDateEnrollment.setErrors({ invalid: true })

      return
    }
    if (!f.endDateEnrollment || !moment(f.endDateEnrollment).isValid()) {
      this.form.controls.endDateEnrollment.markAsDirty()
      this.form.controls.endDateEnrollment.setErrors({ invalid: true })

      return
    }
    if (
      moment(f.startDateEnrollment).valueOf() >=
      moment(f.endDateEnrollment).valueOf()
    ) {
      this.form.controls.endDateEnrollment.markAsDirty()
      this.form.controls.endDateEnrollment.setErrors({ invalid: true })

      return
    }

    if (!f.availableFromDate || !moment(f.availableFromDate).isValid()) {
      this.form.controls.availableFromDate.markAsDirty()
      this.form.controls.availableFromDate.setErrors({ invalid: true })

      return
    }
    if (!f.availableTillDate || !moment(f.availableTillDate).isValid()) {
      this.form.controls.availableTillDate.markAsDirty()
      this.form.controls.availableTillDate.setErrors({ invalid: true })

      return
    }
    if (
      moment(f.availableFromDate).valueOf() >=
      moment(f.availableTillDate).valueOf()
    ) {
      this.form.controls.availableTillDate.markAsDirty()
      this.form.controls.availableTillDate.setErrors({ invalid: true })

      return
    }

    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsDirty()
      })
      scrollToFirstInvalidControl(this.elementRef, this.notificationService)
      console.log('%cForm is invalid', 'color: red; font-weight: bold;')
      return
    }

    this.onSubmit.emit({
      ...f,
      endDate:
        this.course?.id && f.endDate
          ? moment(f.endDate).subtract(1, 'minute').toISOString()
          : f.endDate
    })
  }

  public attemptSubmit(): void {
    Object.keys(this.form.controls).map((controlName) =>
      this.form.controls[controlName].markAsDirty()
    )

    this.formRef.ngSubmit.emit(undefined)
  }

  public onSearchProduct(value) {
    this.store.dispatch(
      productActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            isActive: true,
            isCourse: true,
            description: { [CondOperator.CONTAINS_LOW]: value.term }
          },
          join: [{ field: 'translations' }, { field: 'tags' }]
        })
      })
    )
  }
}
